import { Box, CircularProgress, styled, Typography } from '@mui/material';
import { IComment, IOrganizationConfig, ITicket, ITicketTrailEvent } from '@tymely/atoms';
import { LlmTrailEvent } from '@tymely/atoms/llmTrail.atoms';
import React, { useState, useMemo } from 'react';
import { useAllTicketEventsQuery, useTicket, useTicketOrganization } from '@tymely/services';

import { useEventMode } from '../../EventModeContext';
import ChatEvent from './ChatEvent';
import Comment from './Comment';
import ConversationMessages from './ConversationMessages';
import { getConversationEvents, sortItems, getDatesEventsPerComment, Message, stripHtml, normalizeText } from './utils';
import EventItem from './EventItem';
import { ToolInfo } from './ChatEvent/types';
import ThinkingPart from './ChatEvent/components/ThinkingPart';
import { EVENT_KINDS, TICKET_OPERATIONS } from '../../constants';

type CommentEvents = { isLoading: boolean; data?: ITicketTrailEvent[] | LlmTrailEvent[] };

export type DiscussionProps = {
    ticket: ITicket;
    highlightText?: string;
    events?: CommentEvents;
    selectedCommentId?: number;
    commentIdToFocusOn?: number;
    onUntag?: (comment: IComment) => void;
    onSelectComment?: (comment: IComment['id']) => void;
    onDeselectComment?: () => void;
    organizationConfig?: IOrganizationConfig;
};

const _Date = styled(Box)`
    position: sticky;
    top: ${({ theme }) => theme.spacing(1)};
    z-index: 1;
    display: flex;
    margin-top: ${({ theme }) => theme.spacing(1)};
    margin-bottom: 0;
    justify-content: center;
`;

const _DateLabel = styled(Typography)`
    border: 1px solid #ddd;
    padding: ${({ theme }) => theme.spacing(0, 1)};
    border-radius: 100px;
    background: #fff;
`;

const _ProgressContainer = styled(Box)`
    width: '100%';
    display: 'flex';
    justify-content: 'center';
`;

type CommentGroupProps = DiscussionProps & {
    date: string;
    items: (ITicketTrailEvent | IComment | LlmTrailEvent)[];
    lastEvent?: ITicketTrailEvent | LlmTrailEvent;
};

const CommentGroup = ({
    items,
    highlightText,
    selectedCommentId,
    commentIdToFocusOn,
    onSelectComment,
    onDeselectComment,
    onUntag,
    events,
    date,
    ticket: propsTicket,
    organizationConfig: propsOrganizationConfig,
}: CommentGroupProps) => {
    const { eventMode } = useEventMode();
    const orgFromHook = useTicketOrganization();
    const config = propsOrganizationConfig ?? orgFromHook?.config;
    const globalPromptName = config?.conversation?.global_guidelines_prompt_name;
    const clientPromptName = config?.conversation?.client_guidelines_prompt_name;

    const [openConversationId, setOpenConversationId] = useState<number | null>(null);
    const ticketFromHook = useTicket();
    const ticket = propsTicket ?? ticketFromHook;
    const [showThinkingById, setShowThinkingById] = useState<Record<number, boolean>>({});

    const { data: ticketEventsData } = useAllTicketEventsQuery(ticket?.id);

    const conversationEvents = useMemo(() => {
        if (eventMode !== 'chatEventsMode' || !ticket) return [];
        return getConversationEvents(items, ticket.organization_id);
    }, [items, eventMode, ticket]);

    const allItems = useMemo(() => {
        const allEvents: (ITicketTrailEvent | IComment | LlmTrailEvent)[] = [...items, ...conversationEvents];
        return sortItems(allEvents);
    }, [items, conversationEvents, eventMode]);

    const { globalSystemPrompt, clientSystemPrompt } = useMemo(() => {
        return {
            globalSystemPrompt: items?.find(
                (item) =>
                    'operation' in item &&
                    item.operation === TICKET_OPERATIONS.CHAT_CONVERSER &&
                    item.additional_data?.audit_log?.kind === EVENT_KINDS.SYSTEM_PROMPT &&
                    item.additional_data?.audit_log?.prompt_name === globalPromptName,
            )?.additional_data?.audit_log?.rendered_template,
            clientSystemPrompt: items?.find(
                (item) =>
                    'operation' in item &&
                    item.operation === TICKET_OPERATIONS.CHAT_CONVERSER &&
                    item.additional_data?.audit_log?.kind === EVENT_KINDS.SYSTEM_PROMPT &&
                    item.additional_data?.audit_log?.prompt_name === clientPromptName,
            )?.additional_data?.audit_log?.rendered_template,
        };
    }, [items]);

    const { toolsPerComment } = useMemo(() => {
        return allItems.reduce<{
            toolsPerComment: Record<string | number, ToolInfo[]>;
        }>(
            (acc, item) => {
                if (
                    'conversation_data' in item &&
                    item.conversation_data &&
                    Array.isArray(item.conversation_data.tools) &&
                    item.conversation_data.tools.length > 0
                ) {
                    acc.toolsPerComment[item.id] = (item.conversation_data.tools as ToolInfo[]).map((tool) => ({
                        name: tool.name,
                        description: tool.description,
                    }));
                }
                return acc;
            },
            { toolsPerComment: {} },
        );
    }, [allItems]);

    const policyEvaluationEventsPerComment = useMemo(() => {
        const comments = items.filter((item): item is IComment => 'inquiry_date' in item);
        return getDatesEventsPerComment(ticketEventsData, comments);
    }, [ticketEventsData, items]);

    const thinkingContentPerComment = useMemo(() => {
        let lastThinkingPart: string | null = null;
        let lastPlainTextPart: string | null = null;
        const mapping: Record<number, string> = {};

        for (const comment of items.filter((item): item is IComment => 'inquiry_date' in item)) {
            const messages = comment.conversation_data?.messages;
            if (Array.isArray(messages) && messages.length > 0) {
                const lastMessage = messages[messages.length - 1] as Message;
                if (Array.isArray(lastMessage.parts)) {
                    const thinkingPart = lastMessage.parts.find((p: any) => p.part_kind === 'thinking');
                    const textPart = lastMessage.parts.find((p: any) => p.part_kind === 'text');
                    if (thinkingPart && textPart && typeof textPart.content === 'string') {
                        lastThinkingPart = thinkingPart.content as string;
                        lastPlainTextPart = normalizeText(textPart.content as string);
                    }
                }
            }
            if (lastThinkingPart && lastPlainTextPart && typeof comment.body === 'string') {
                const plainBody = normalizeText(stripHtml(comment.body));
                if (plainBody.includes(lastPlainTextPart) || lastPlainTextPart.includes(plainBody)) {
                    mapping[comment.id] = lastThinkingPart;
                }
            }
        }
        return mapping;
    }, [items]);

    return (
        <Box>
            <_Date>
                <_DateLabel variant="caption">{date}</_DateLabel>
            </_Date>
            {events?.isLoading && (
                <_ProgressContainer>
                    <CircularProgress size={24} />
                </_ProgressContainer>
            )}
            {allItems.map((item, index) => {
                if ('inquiry_date' in item) {
                    const showThinking = showThinkingById[item.id] || false;
                    return (
                        <Box key={item.id} position="relative">
                            <Comment
                                comment={item}
                                highlightText={highlightText}
                                selected={selectedCommentId === item.id}
                                focusedOn={commentIdToFocusOn === item.id}
                                onSelect={onSelectComment}
                                onDeselect={onDeselectComment}
                                onUntag={onUntag}
                                onToggleConversationMessages={() =>
                                    setOpenConversationId(openConversationId === item.id ? null : item.id)
                                }
                                showConversationMessages={openConversationId === item.id}
                                policyEvaluationEvents={policyEvaluationEventsPerComment[item.id]}
                                thinkingContent={thinkingContentPerComment[item.id]}
                                onToggleThinking={() =>
                                    setShowThinkingById((prev) => ({ ...prev, [item.id]: !prev[item.id] }))
                                }
                                organizationConfig={config}
                            />
                            {thinkingContentPerComment[item.id] && showThinking && (
                                <ThinkingPart content={thinkingContentPerComment[item.id]} />
                            )}
                            {openConversationId === item.id &&
                                item.conversation_data &&
                                Array.isArray(item.conversation_data.messages) && (
                                    <Box mt={1} mb={2}>
                                        <ConversationMessages
                                            conversationData={item.conversation_data as { messages: any[] }}
                                        />
                                    </Box>
                                )}
                        </Box>
                    );
                }
                if (
                    eventMode === 'chatEventsMode' &&
                    item.additional_data?.audit_log?.prompt_name !== globalPromptName &&
                    item.additional_data?.audit_log?.prompt_name !== clientPromptName
                ) {
                    return (
                        <ChatEvent
                            key={index}
                            globalSystemPrompt={globalSystemPrompt}
                            clientSystemPrompt={clientSystemPrompt}
                            event={item as ITicketTrailEvent}
                            toolsPerComment={toolsPerComment[item?.additional_data.audit_log?.comment_id]}
                        />
                    );
                } else if (eventMode !== 'chatEventsMode') {
                    return <EventItem key={index} event={item} />;
                }
                return null;
            })}
        </Box>
    );
};

export default CommentGroup;
